// src/services/albumExtensionInvoiceTemplates.js
// HTML template generation for album extension invoices (website version)

import { PLATFORM_DETAILS } from './albumExtensionInvoiceService';
import { amountInWords as wordsFor, invoiceCurrency, moneyOnInvoice, placeOfSupply, supplyNote } from './invoiceMoney';

/**
 * Format date for invoice display
 */
const formatInvoiceDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Generate Album Extension Invoice HTML
 */
export const generateAlbumExtensionInvoiceHTML = (invoice) => {
  const {
    invoice_number,
    album_name,
    extension_duration_days,
    previous_expiry_date,
    new_expiry_date,
    base_amount,
    gst_type,
    gst_rate = 18,
    cgst,
    sgst,
    igst,
    total_amount,
    customer_name,
    customer_email,
    customer_phone,
    generated_at,
    hsn_sac = '998315',
    seller_name = PLATFORM_DETAILS.name,
    seller_trade_name = PLATFORM_DETAILS.tradeName,
    seller_gstin = PLATFORM_DETAILS.gstin,
    seller_pan = PLATFORM_DETAILS.pan,
    seller_address = PLATFORM_DETAILS.address,
    seller_state = PLATFORM_DETAILS.state,
    seller_state_code = PLATFORM_DETAILS.stateCode,
    seller_email = PLATFORM_DETAILS.email,
  } = invoice;

  const invoiceDate = formatInvoiceDate(generated_at);
  const currency = invoiceCurrency(invoice);
  const money = (v) => moneyOnInvoice(v, currency);
  const amountInWords = wordsFor(total_amount, currency);
  const extensionMonths = Math.round(extension_duration_days / 30);

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=794, initial-scale=1.0">
  <title>Album Extension Invoice - ${invoice_number}</title>
  <style>
    @page { size: A4; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      width: 794px;
      height: 1123px;
      margin: 0;
      padding: 0;
      background: white;
      overflow: hidden;
    }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      line-height: 1.4;
      color: #333;
      font-size: 11px;
    }
    .invoice-container {
      width: 794px;
      height: 1123px;
      margin: 0;
      padding: 50px 55px 40px 55px;
      background: white;
      overflow: hidden;
    }
    .header {
      text-align: center;
      border-bottom: 2px solid #FFD93D;
      padding-bottom: 8px;
      margin-bottom: 16px;
    }
    .header h1 {
      color: #FFD93D;
      font-size: 22px;
      margin-bottom: 2px;
      font-weight: 700;
    }
    .header .trade-name {
      font-size: 10px;
      color: #666;
      margin-bottom: 4px;
    }
    .header .doc-type {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-top: 5px;
    }
    .header .service-type {
      font-size: 9px;
      color: #888;
      margin-top: 2px;
    }
    .invoice-info {
      display: flex;
      justify-content: space-between;
      margin-bottom: 12px;
      padding: 8px 10px;
      background: #f8f9fa;
      border-radius: 5px;
      font-size: 10px;
    }
    .invoice-info div {
      flex: 1;
    }
    .invoice-info .right {
      text-align: right;
    }
    .invoice-info strong {
      color: #555;
    }
    .album-banner {
      background: linear-gradient(135deg, #FFD93D22 0%, #FFC10722 100%);
      border: 1px solid #FFD93D44;
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 12px;
    }
    .album-banner .label {
      font-size: 8px;
      color: #F59E0B;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
    }
    .album-banner .album-title {
      font-size: 13px;
      font-weight: 600;
      color: #333;
    }
    .parties-section {
      display: flex;
      gap: 15px;
      margin-bottom: 12px;
    }
    .party-box {
      flex: 1;
      border: 1px solid #ddd;
      border-radius: 5px;
      padding: 10px;
      font-size: 10px;
    }
    .party-box .party-title {
      font-size: 9px;
      font-weight: 600;
      text-transform: uppercase;
      color: #FFD93D;
      margin-bottom: 6px;
      letter-spacing: 0.5px;
    }
    .party-box .name {
      font-weight: 600;
      margin-bottom: 2px;
      font-size: 11px;
    }
    .party-box .detail {
      margin-bottom: 1px;
      color: #555;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 12px;
      font-size: 10px;
    }
    table th {
      background: #f8f9fa;
      padding: 8px 6px;
      text-align: left;
      font-weight: 600;
      border: 1px solid #ddd;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    table td {
      padding: 8px 6px;
      border: 1px solid #ddd;
    }
    table th.right, table td.right {
      text-align: right;
    }
    table th.center, table td.center {
      text-align: center;
    }
    .item-description {
      font-weight: 600;
      margin-bottom: 2px;
    }
    .item-details {
      font-size: 9px;
      color: #666;
      line-height: 1.3;
    }
    .total-row {
      font-weight: 600;
      background: #f8f9fa;
      font-size: 11px;
    }
    .amount-in-words {
      background: #f8f9fa;
      padding: 8px 10px;
      border-radius: 5px;
      margin-bottom: 12px;
      font-size: 10px;
    }
    .amount-in-words .label {
      font-weight: 600;
      color: #555;
      margin-right: 6px;
    }
    .footer-note {
      font-size: 9px;
      color: #666;
      text-align: center;
      margin-top: 16px;
      padding-top: 12px;
      border-top: 1px solid #ddd;
    }
    .footer-note p {
      margin-bottom: 2px;
    }
    .gst-note {
      font-size: 8px;
      color: #999;
      margin-top: 8px;
      font-style: italic;
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <!-- Header -->
    <div class="header">
      <h1>${seller_name}</h1>
      <div class="trade-name">(${seller_trade_name})</div>
      <div class="doc-type">TAX INVOICE</div>
      <div class="service-type">Album Storage Extension Service</div>
    </div>

    <!-- Invoice Info -->
    <div class="invoice-info">
      <div>
        <strong>Invoice No:</strong> ${invoice_number.replace(/-/g, '/')}
      </div>
      <div class="right">
        <strong>Date:</strong> ${invoiceDate}
      </div>
    </div>

    <!-- Album Banner -->
    <div class="album-banner">
      <div class="label">Album</div>
      <div class="album-title">${album_name || 'N/A'}</div>
    </div>

    <!-- Parties Section -->
    <div class="parties-section">
      <!-- Seller Details -->
      <div class="party-box">
        <div class="party-title">Service Provider</div>
        <div class="name">${seller_name}</div>
        <div class="detail">${seller_address}</div>
        <div class="detail"><strong>GSTIN:</strong> ${seller_gstin}</div>
        <div class="detail"><strong>PAN:</strong> ${seller_pan}</div>
        <div class="detail"><strong>State:</strong> ${seller_state} (${seller_state_code})</div>
        <div class="detail"><strong>Email:</strong> ${seller_email}</div>
      </div>

      <!-- Buyer Details -->
      <div class="party-box">
        <div class="party-title">Customer (Bill To)</div>
        <div class="name">${customer_name || 'N/A'}</div>
        ${customer_email ? `<div class="detail"><strong>Email:</strong> ${customer_email}</div>` : ''}
        ${customer_phone ? `<div class="detail"><strong>Phone:</strong> ${customer_phone}</div>` : ''}
        <div class="detail"><strong>Place of Supply:</strong> ${placeOfSupply(invoice)}</div>
      </div>
    </div>

    <!-- Items Table -->
    <table>
      <thead>
        <tr>
          <th style="width: 50px;" class="center">#</th>
          <th>Description of Service</th>
          <th style="width: 90px;" class="center">HSN/SAC</th>
          <th style="width: 80px;" class="right">Amount (${currency})</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="center">1</td>
          <td>
            <div class="item-description">Album Storage Extension - ${extensionMonths} Month${extensionMonths > 1 ? 's' : ''}</div>
            <div class="item-details">
              Album: ${album_name || 'N/A'}<br>
              ${previous_expiry_date ? `Previous Expiry: ${formatInvoiceDate(previous_expiry_date)}<br>` : ''}
              New Expiry: ${formatInvoiceDate(new_expiry_date)}
            </div>
          </td>
          <td class="center">${hsn_sac}</td>
          <td class="right">${money(base_amount).replace(/^\S+ /, '')}</td>
        </tr>
        <tr>
          <td colspan="3" class="right"><strong>Subtotal</strong></td>
          <td class="right">${money(base_amount).replace(/^\S+ /, '')}</td>
        </tr>
        ${gst_type === 'intrastate' ? `
        <tr>
          <td colspan="3" class="right">CGST @ ${gst_rate / 2}%</td>
          <td class="right">${money(cgst).replace(/^\S+ /, '')}</td>
        </tr>
        <tr>
          <td colspan="3" class="right">SGST @ ${gst_rate / 2}%</td>
          <td class="right">${money(sgst).replace(/^\S+ /, '')}</td>
        </tr>
        ` : `
        <tr>
          <td colspan="3" class="right">IGST @ ${gst_rate}%</td>
          <td class="right">${money(igst).replace(/^\S+ /, '')}</td>
        </tr>
        `}
        <tr class="total-row">
          <td colspan="3" class="right"><strong>Total Amount</strong></td>
          <td class="right"><strong>${money(total_amount)}</strong></td>
        </tr>
      </tbody>
    </table>

    <!-- Amount in Words -->
    <div class="amount-in-words">
      <span class="label">Amount in Words:</span>
      <span>${amountInWords} Only</span>
    </div>

    <!-- GST Note -->
    <div class="gst-note">
      Note: This is a system-generated invoice for album storage extension service.
      ${supplyNote(invoice)}
    </div>

    <!-- Footer -->
    <div class="footer-note">
      <p><strong>Thank you for using Invits!</strong></p>
      <p>For support, contact us at ${seller_email}</p>
      <p style="margin-top: 6px; font-size: 8px;">This is a computer-generated invoice and does not require a physical signature.</p>
    </div>
  </div>
</body>
</html>
  `;

  return html.trim();
};
